import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import {
  INCLUDE_ACCOUNTS,
  INCLUDE_ALL_ACCOUNTS,
  AUTH_ACCOUNTS,
} from "@common/constants";
import { useLocalStorage } from "@services/common/hooks/useLocalStorage";
import { Account } from "../types";
import { sortByAccountName } from "@common/components/Header/utils/utils";
import { getUserAccounts } from "..";

export const useUserSelectedAccounts = (): [
  string[],
  boolean | null,
  Account[],
  (accounts: string[] | undefined) => void,
  (includeAll: boolean | undefined) => void,
  (account: Account | null | undefined) => void,
  (account?: Account) => void,
  (accountIds: string[]) => void,
  () => void
] => {
  const [user] = useAuthenticatedUser();
  const userKey = user?.id ?? "unauthorized";

  const includeAccountsKey = `${INCLUDE_ACCOUNTS}_${userKey}`;
  const includeAllKey = `${INCLUDE_ALL_ACCOUNTS}_${userKey}`;
  const accountsKey = `${AUTH_ACCOUNTS}_${userKey}`;

  const [accounts, setAccounts] = useLocalStorage<string[]>(includeAccountsKey);
  const [includeAll, setIncludeAll] = useLocalStorage<boolean>(includeAllKey);
  const [allAccounts, setAllAccounts] = useLocalStorage<Account[]>(accountsKey);

  const addAccount = (account: Account | null | undefined) => {
    if (account) {
      const temp = [...(allAccounts ?? []), account];
      setAllAccounts(temp.sort(sortByAccountName));
      setAccounts([...(accounts ?? []), account.id]);
    }
  };

  const updateAccount = (account?: Account) => {
    if (account) {
      const temp = (allAccounts ?? []).map((a) =>
        a.id === account.id ? account : a
      );
      setAllAccounts(temp.sort(sortByAccountName));
    }
  };

  const removeAccount = (accountIds: string[]) => {
    if (accountIds) {
      const temp = (allAccounts ?? []).filter(
        (a) => !accountIds.includes(a.id)
      );
      setAllAccounts(temp.sort(sortByAccountName));
      setAccounts([...(accounts ?? []).filter((a) => !accountIds.includes(a))]);
    }
  };

  const refreshAccounts = async () => {
    const newAccounts = await getUserAccounts(userKey);
    setAllAccounts(newAccounts.sort(sortByAccountName));
    let accountIds = newAccounts.map((a) => a.id);
    const temp = (accounts ?? []).filter((id) => {
      return accountIds.includes(id);
    });
    setAccounts(temp);
  };

  return [
    accounts ?? [],
    includeAll ?? null,
    allAccounts ?? [],
    setAccounts,
    setIncludeAll,
    addAccount,
    updateAccount,
    removeAccount,
    refreshAccounts,
  ];
};
